import {
  addToCartExperimentsTrackProductOnSuccess,
  type AddToCartSource,
} from '../../experiments/add-to-cart'
import { userApiSwrRefetchMiniCart } from '../../user-data'
import { handleAddToCartErrorApm } from '../api/addToCartErrorApmLogger'
import {
  type CartIntermediateAddToCartProps,
  useCartIntermediateApiSwrProductAddToCartMutation,
} from '../api/swr/useCartIntermediateApiSwrProductAddToCartMutation'
import { cartIntermediateFreezeExpaCookie } from '../cookie-handling/cartIntermediateFreezeExpaCookie'
import { updateIsDataSuccessfullyAddedToCartFromResponse } from '../helpers/updateIsDataSuccessfullyAddedToCartFromResponse'
import { cartIntermediateAccessorGetProductFamilyIds } from '../model/cartIntermediateAccessorGetProductFamilyIds'
import { cartIntermediateAccessorGetProductTypeContext } from '../model/cartIntermediateAccessorGetProductTypeContext'
import { type CartIntermediateMutationResponseData } from '../model/CartIntermediateContextValue'
import {
  cartIntermediateAccessorGetDrawerMessages,
  cartIntermediateAccessorGetIsDataSuccessfullyAddedToCart,
} from '../model/cartIntermediateMutationResponseDataAccessor'

import { useCartIntermediateContext } from './useCartIntermediateContext'

export const useProductAddToCart = ({
  currentPrescriptionType,
  offerId,
  product,
  quantity,
  redpoints,
  rxByPrincipleSelectedReason,
  sellerData,
  source,
}: CartIntermediateAddToCartProps & {
  source: AddToCartSource
}): {
  handleAddToCart: () => Promise<void>
  isMutating: boolean
} => {
  const productAddToCartMutation =
    useCartIntermediateApiSwrProductAddToCartMutation({
      currentPrescriptionType,
      offerId,
      quantity,
      redpoints,
      rxByPrincipleSelectedReason,
    })
  const {
    openCartIntermediateDrawerWithData,
    resetCartIntermediateContext,
    setIsDataSuccessfullyAddedToCart,
    specialOffersCollapsibleControls: { handleClose: closeSpecialOffers },
  } = useCartIntermediateContext()
  const handleAddToCart = async (): Promise<void> => {
    resetCartIntermediateContext()

    if (!product || !offerId) {
      return
    }

    const products = [
      {
        id: offerId,
        prescriptionType: currentPrescriptionType,
        product,
        redpoints,
        sellerData,
      },
    ]

    const handlePostAddToCart = (
      response: CartIntermediateMutationResponseData,
    ): void => {
      const drawerMessages = cartIntermediateAccessorGetDrawerMessages(response)
      updateIsDataSuccessfullyAddedToCartFromResponse(
        response,
        setIsDataSuccessfullyAddedToCart,
      )
      closeSpecialOffers()
      cartIntermediateFreezeExpaCookie()
      openCartIntermediateDrawerWithData(products, drawerMessages, quantity)
    }

    const transaction = globalThis.clientSideApmInstance?.startTransaction(
      'Add to cart',
      'custom',
      { managed: true },
    )
    let response: CartIntermediateMutationResponseData = null

    try {
      response = await productAddToCartMutation.trigger()
      await userApiSwrRefetchMiniCart()
    } catch (error) {
      handleAddToCartErrorApm(error)
    } finally {
      transaction?.end()
      handlePostAddToCart(response)
    }

    addToCartExperimentsTrackProductOnSuccess({
      isSuccessfullyAddedToCart:
        cartIntermediateAccessorGetIsDataSuccessfullyAddedToCart(response),
      product: {
        offerId,
        productFamilyIds: cartIntermediateAccessorGetProductFamilyIds({
          product,
        }),
        productId: 'UID' in product ? product.UID : product.upid,
        productTypeContext: cartIntermediateAccessorGetProductTypeContext({
          product,
          sellerData,
        }),
        quantity,
      },
      source,
    })
  }

  return {
    handleAddToCart,
    isMutating: productAddToCartMutation.isMutating,
  }
}
